import * as React from 'react'
import {
    Box,
    Chip,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material'

import useAutoTranslate from '../hooks/useAutoTranslate'
import useOfficialGameTexts from '../hooks/useOfficialGameTexts'
import UnitIconFrame from './UnitIconFrame'

const LEGACY_SELECTION_MAP = {
    0: '206',
    1: '229',
    2: '196',
    3: '218',
    4: '602'
}

const normalizeSelectionOption = (option, fallbackValue) => {
    if (option && typeof option === 'object' && !Array.isArray(option)) {
        return {
            label: option.label ?? String(option.value ?? fallbackValue),
            translationKey: String(option.translationKey ?? ''),
            value: String(option.value ?? fallbackValue),
            iconUrl: String(option.iconUrl ?? ''),
            assetKey: String(option.assetKey ?? ''),
            minBarracksLevel: Number.isFinite(Number(option.minBarracksLevel)) ? Number(option.minBarracksLevel) : 0,
            minLevel: Number.isFinite(Number(option.minLevel)) ? Number(option.minLevel) : 1,
            maxLevel: Number.isFinite(Number(option.maxLevel)) ? Number(option.maxLevel) : 1,
            availableLevels: Array.isArray(option.availableLevels) ?
                option.availableLevels
                    .map(level => Number.parseInt(String(level), 10))
                    .filter(Number.isFinite)
                    .sort((left, right) => left - right) :
                [],
            role: option.role === 'ranged' ? 'ranged' : 'melee',
            fightType: Number(option.fightType) === 1 ? 1 : 0,
            requiresResearch: Boolean(option.requiresResearch)
        }
    }

    return {
        label: String(option ?? fallbackValue),
        translationKey: '',
        value: String(fallbackValue),
        iconUrl: '',
        assetKey: '',
        minBarracksLevel: 0,
        minLevel: 1,
        maxLevel: 1,
        availableLevels: [],
        role: 'melee',
        fightType: 0,
        requiresResearch: false
    }
}

const normalizeTroopType = value => {
    if ([undefined, null, ''].includes(value))
        return ''

    const normalizedValue = String(value)
    return LEGACY_SELECTION_MAP[normalizedValue] ?? normalizedValue
}

const normalizeLevel = value => {
    if ([undefined, null, ''].includes(value))
        return ''

    const parsed = Number.parseInt(String(value), 10)
    return Number.isFinite(parsed) && parsed >= 0 ? String(parsed) : ''
}

const normalizeSlotSize = value => {
    if ([undefined, null, ''].includes(value))
        return ''

    const parsed = Number.parseInt(String(value), 10)
    return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : ''
}

const hasPerTargetConfig = value =>
    Boolean(value) &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).length > 0

const normalizeTargetConfig = value => {
    const troopType = normalizeTroopType(value?.troopType)
    const level = normalizeLevel(value?.level)
    const slotSize = normalizeSlotSize(value?.slotSize)
    const hasValues = Boolean(troopType || level)

    return {
        enabled: typeof value?.enabled === 'boolean' ? value.enabled : hasValues,
        troopType,
        level,
        slotSize
    }
}

const hasMeaningfulTargetConfig = value =>
    Boolean(value?.enabled || value?.troopType || value?.level || value?.slotSize)

const buildFallbackTargetConfig = (legacyLevel, legacyTroopType) => {
    const troopType = normalizeTroopType(legacyTroopType)
    const level = normalizeLevel(legacyLevel)

    if (!troopType || !level)
        return undefined

    return {
        enabled: true,
        troopType,
        level,
        slotSize: ''
    }
}

const buildTargetFallbacks = (targets, fallbackConfig) => {
    if (!fallbackConfig)
        return {}

    return Object.fromEntries(
        targets.map(target => [target.key, { ...fallbackConfig }])
    )
}

const buildUnknownTargets = configs =>
    Object.keys(configs || {}).map(key => ({
        key,
        label: `Saved target ${key}`,
        detail: '',
        badge: 'Saved',
        barracksState: 'unknown'
    }))

const buildLevelOptions = option => {
    if (Array.isArray(option?.availableLevels) && option.availableLevels.length > 0)
        return option.availableLevels.map(level => String(level))

    const minLevel = Number(option?.minLevel)
    const maxLevel = Number(option?.maxLevel)

    if (!Number.isFinite(minLevel) || !Number.isFinite(maxLevel))
        return []

    const start = Math.min(minLevel, maxLevel)
    const end = Math.max(minLevel, maxLevel)
    const levels = []

    for (let level = start; level <= end; level += 1)
        levels.push(String(level))

    return levels
}

const resolveDisplayedLevel = (currentLevel, option) => {
    const levels = buildLevelOptions(option)
    if (levels.length === 0)
        return ''

    const normalizedCurrentLevel = normalizeLevel(currentLevel)
    const hasPositiveLevel = levels.some(level => Number(level) > 0)
    if (!levels.includes(normalizedCurrentLevel))
        return levels[levels.length - 1]
    if (normalizedCurrentLevel === '0' && hasPositiveLevel)
        return levels[levels.length - 1]

    return normalizedCurrentLevel
}

const buildOptionMetaLabel = (option, t) => {
    const parts = [
        `${t('Barracks Level')} ${option.minBarracksLevel}`,
        option.fightType === 0 ? t('Attack') : t('Defense'),
        option.role === 'ranged' ? t('Ranged') : t('Melee')
    ]

    if (option.requiresResearch)
        parts.push(t('Requires research'))

    return parts.join(' | ')
}

export default function BarracksCastleConfigurator({
    pluginData,
    parentKey,
    userPlugins,
    onChange,
    availableTargets,
    appLanguage = 'en'
}) {
    userPlugins[parentKey] ??= {}

    const rawConfigs = React.useMemo(() => (
        userPlugins[parentKey][pluginData.key] && typeof userPlugins[parentKey][pluginData.key] === 'object' ?
            userPlugins[parentKey][pluginData.key] :
            {}
    ), [parentKey, pluginData.key, userPlugins])
    const legacyLevel = userPlugins[parentKey]?.level
    const legacyTroopType = userPlugins[parentKey]?.troopType
    const fallbackConfig = React.useMemo(
        () => buildFallbackTargetConfig(legacyLevel, legacyTroopType),
        [legacyLevel, legacyTroopType]
    )

    const targets = React.useMemo(() => {
        const nextTargets = Array.isArray(availableTargets) && availableTargets.length > 0 ?
            availableTargets :
            buildUnknownTargets(rawConfigs)

        return nextTargets.map(target => ({
            ...target,
            key: String(target.key)
        }))
    }, [availableTargets, rawConfigs])

    const troopOptions = React.useMemo(
        () => (pluginData.selection ?? []).map((option, index) => normalizeSelectionOption(option, index)),
        [pluginData.selection]
    )

    const troopOptionsByValue = React.useMemo(
        () => new Map(troopOptions.map(option => [option.value, option])),
        [troopOptions]
    )

    const troopLabelTexts = React.useMemo(
        () => [...new Set(
            troopOptions
                .map(option => String(option?.label ?? '').trim())
                .filter(Boolean)
        )],
        [troopOptions]
    )
    const troopTranslationKeys = React.useMemo(
        () => [...new Set(
            troopOptions
                .map(option => String(option?.translationKey ?? '').trim())
                .filter(Boolean)
        )],
        [troopOptions]
    )

    const t = useAutoTranslate(appLanguage, [
        'Recruitment Targets',
        'Troop Type',
        'Level',
        'Disabled',
        'Main Castle',
        'Castle',
        'Outpost',
        'No castles available yet. Start this bot once to load castles and outposts.',
        'Legacy global setup will be copied to all targets the first time you edit one.',
        'Coordinates',
        'Not loaded yet',
        'Saved',
        'Barracks Level',
        'No Barracks',
        'Could not read Barracks',
        'Loading Barracks...',
        'Only troops currently recruitable in this location are shown.',
        'No valid troops found for this location.',
        'Requires research',
        'Attack',
        'Defense',
        'Melee',
        'Ranged',
        'Unavailable here',
        'Saved selection is no longer valid for this location.',
        ...troopLabelTexts
    ])
    const getOfficialGameText = useOfficialGameTexts(appLanguage, troopTranslationKeys)
    const getTroopLabel = React.useCallback(option => {
        const fallbackLabel = String(option?.label ?? '')
        const translationKey = String(option?.translationKey ?? '').trim()
        return (translationKey ? getOfficialGameText(translationKey, '') : '') || t(fallbackLabel)
    }, [getOfficialGameText, t])

    const getDisplayedConfig = React.useCallback(targetKey => {
        if (hasPerTargetConfig(rawConfigs))
            return normalizeTargetConfig(rawConfigs[targetKey])

        return normalizeTargetConfig(fallbackConfig)
    }, [fallbackConfig, rawConfigs])

    const getTargetOptions = React.useCallback(target => {
        if (Array.isArray(target?.availableRecruitmentOptions))
            return target.availableRecruitmentOptions.map((option, index) => normalizeSelectionOption(option, index))

        const barracksLevel = Number(target?.barracksLevel)
        if (!Number.isFinite(barracksLevel) || barracksLevel <= 0)
            return []

        return troopOptions.filter(option => option.minBarracksLevel <= barracksLevel)
    }, [troopOptions])

    const getLevelChoices = React.useCallback((target, troopTypeValue) => {
        const targetOptions = getTargetOptions(target)
        const selectedOption = targetOptions.find(option => option.value === troopTypeValue) ??
            troopOptionsByValue.get(troopTypeValue)

        return buildLevelOptions(selectedOption)
    }, [getTargetOptions, troopOptionsByValue])

    const updateConfigs = React.useCallback((updater) => {
        const nextConfigs = hasPerTargetConfig(rawConfigs) ?
            Object.fromEntries(
                Object.entries(rawConfigs).map(([key, value]) => [key, normalizeTargetConfig(value)])
            ) :
            buildTargetFallbacks(targets, fallbackConfig)

        const resolvedConfigs = updater(nextConfigs)
        const nextUserPlugins = {
            ...userPlugins,
            [parentKey]: {
                ...userPlugins[parentKey],
                [pluginData.key]: resolvedConfigs
            }
        }

        onChange(nextUserPlugins)
    }, [fallbackConfig, onChange, parentKey, pluginData.key, rawConfigs, targets, userPlugins])

    const handleTroopChange = React.useCallback((target, nextTroopType) => {
        updateConfigs(currentConfigs => {
            const nextConfigs = { ...currentConfigs }
            const currentTarget = normalizeTargetConfig(nextConfigs[target.key])
            currentTarget.troopType = normalizeTroopType(nextTroopType)

            if (!currentTarget.troopType) {
                currentTarget.level = ''
            } else {
                const targetOptions = getTargetOptions(target)
                const selectedOption = targetOptions.find(option => option.value === currentTarget.troopType) ??
                    troopOptionsByValue.get(currentTarget.troopType)
                currentTarget.level = resolveDisplayedLevel(currentTarget.level, selectedOption)
            }

            if (!hasMeaningfulTargetConfig(currentTarget))
                delete nextConfigs[target.key]
            else
                nextConfigs[target.key] = currentTarget

            return nextConfigs
        })
    }, [getTargetOptions, troopOptionsByValue, updateConfigs])

    const handleLevelChange = React.useCallback((targetKey, nextValue) => {
        updateConfigs(currentConfigs => {
            const nextConfigs = { ...currentConfigs }
            const currentTarget = normalizeTargetConfig(nextConfigs[targetKey])
            currentTarget.level = normalizeLevel(nextValue)

            if (!hasMeaningfulTargetConfig(currentTarget))
                delete nextConfigs[targetKey]
            else
                nextConfigs[targetKey] = currentTarget

            return nextConfigs
        })
    }, [updateConfigs])

    React.useEffect(() => {
        if (targets.length === 0)
            return

        let hasChanges = false
        const nextConfigs = hasPerTargetConfig(rawConfigs) ?
            Object.fromEntries(
                Object.entries(rawConfigs).map(([key, value]) => [key, normalizeTargetConfig(value)])
            ) :
            buildTargetFallbacks(targets, fallbackConfig)

        targets.forEach(target => {
            const currentTarget = normalizeTargetConfig(nextConfigs[target.key])
            if (!currentTarget.troopType)
                return

            const targetOptions = getTargetOptions(target)
            if (!targetOptions.some(option => option.value === currentTarget.troopType))
                return

            const selectedOption = targetOptions.find(option => option.value === currentTarget.troopType)
            const nextLevel = resolveDisplayedLevel(currentTarget.level, selectedOption)

            if (nextLevel !== currentTarget.level) {
                nextConfigs[target.key] = {
                    ...currentTarget,
                    level: nextLevel
                }
                hasChanges = true
            }
        })

        if (!hasChanges)
            return

        onChange({
            ...userPlugins,
            [parentKey]: {
                ...userPlugins[parentKey],
                [pluginData.key]: nextConfigs
            }
        })
    }, [
        fallbackConfig,
        getLevelChoices,
        getTargetOptions,
        onChange,
        parentKey,
        pluginData.key,
        rawConfigs,
        targets,
        userPlugins
    ])

    if (targets.length === 0) {
        return (
            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    borderColor: 'rgba(255,255,255,0.08)',
                    backgroundColor: 'rgba(255,255,255,0.03)'
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    {t('No castles available yet. Start this bot once to load castles and outposts.')}
                </Typography>
            </Paper>
        )
    }

    return (
        <Box sx={{ display: 'grid', gap: 1.35 }}>
            <Typography variant="subtitle2" sx={{ color: 'primary.light' }}>
                {t('Recruitment Targets')}
            </Typography>

            {!hasPerTargetConfig(rawConfigs) && fallbackConfig && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('Legacy global setup will be copied to all targets the first time you edit one.')}
                </Typography>
            )}

            {targets.map(target => {
                const targetConfig = getDisplayedConfig(target.key)
                const targetOptions = getTargetOptions(target)
                const selectedOption = targetOptions.find(option => option.value === targetConfig.troopType) ??
                    troopOptionsByValue.get(targetConfig.troopType)
                const levelChoices = getLevelChoices(target, targetConfig.troopType)
                const hasBarracks = target.barracksState === 'ready' && Number(target.barracksLevel) > 0
                const hasInvalidSavedSelection = Boolean(targetConfig.troopType) &&
                    Boolean(selectedOption) &&
                    !targetOptions.some(option => option.value === targetConfig.troopType)

                let barracksStatusLabel = t('Loading Barracks...')
                if (target.barracksState === 'ready')
                    barracksStatusLabel = `${t('Barracks Level')} ${target.barracksLevel}`
                else if (target.barracksState === 'missing')
                    barracksStatusLabel = t('No Barracks')
                else if (target.barracksState === 'error')
                    barracksStatusLabel = t('Could not read Barracks')

                return (
                    <Paper
                        key={target.key}
                        variant="outlined"
                        sx={{
                            p: 1.5,
                            borderColor: 'rgba(255,255,255,0.08)',
                            backgroundColor: 'rgba(255,255,255,0.03)'
                        }}
                    >
                        <Stack spacing={1.4}>
                            <Box>
                                <Stack direction="row" spacing={0.85} useFlexGap flexWrap="wrap" alignItems="center">
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {target.label}
                                    </Typography>
                                    {(target.badge || target.typeLabel) && (
                                        <Chip
                                            size="small"
                                            label={target.badge ? t(target.badge) : t(target.typeLabel)}
                                            sx={{ height: 22 }}
                                        />
                                    )}
                                    <Chip
                                        size="small"
                                        color={hasBarracks ? 'success' : 'default'}
                                        label={barracksStatusLabel}
                                        sx={{ height: 22 }}
                                    />
                                </Stack>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.35 }}>
                                    {`${t('Coordinates')}: ${target.x}:${target.y}`}
                                </Typography>
                                {hasBarracks && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.35 }}>
                                        {t('Only troops currently recruitable in this location are shown.')}
                                    </Typography>
                                )}
                                {target.barracksState === 'ready' && targetOptions.length === 0 && (
                                    <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.35 }}>
                                        {t('No valid troops found for this location.')}
                                    </Typography>
                                )}
                                {hasInvalidSavedSelection && (
                                    <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.35 }}>
                                        {t('Saved selection is no longer valid for this location.')}
                                    </Typography>
                                )}
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 1.2,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: 'minmax(320px, 1fr) 170px'
                                    }
                                }}
                            >
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={t('Troop Type')}
                                    value={targetConfig.troopType}
                                    onChange={event => handleTroopChange(target, event.target.value)}
                                    sx={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
                                    disabled={!hasInvalidSavedSelection && (!hasBarracks || targetOptions.length === 0)}
                                >
                                    <MenuItem value="">{t('Disabled')}</MenuItem>
                                    {hasInvalidSavedSelection && selectedOption && (
                                        <MenuItem value={targetConfig.troopType}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <UnitIconFrame
                                                    iconUrl={selectedOption.iconUrl}
                                                    itemType="troop"
                                                    level={targetConfig.level || selectedOption.maxLevel}
                                                    size={36}
                                                />
                                                <Box sx={{ display: 'grid', minWidth: 0 }}>
                                                    <Typography variant="body2" noWrap>{getTroopLabel(selectedOption)}</Typography>
                                                    <Typography variant="caption" sx={{ color: 'warning.main' }} noWrap>
                                                        {t('Unavailable here')}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                    )}
                                    {targetOptions.map(option => (
                                        <MenuItem key={`${target.key}-${option.value}`} value={option.value}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                                <UnitIconFrame
                                                    iconUrl={option.iconUrl}
                                                    itemType="troop"
                                                    level={option.maxLevel}
                                                    size={36}
                                                />
                                                <Box sx={{ display: 'grid', minWidth: 0 }}>
                                                    <Typography variant="body2" noWrap>{getTroopLabel(option)}</Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                                                        {buildOptionMetaLabel(option, t)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </TextField>

                                {levelChoices.length > 0 ? (
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label={t('Level')}
                                        value={targetConfig.level}
                                        onChange={event => handleLevelChange(target.key, event.target.value)}
                                        sx={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
                                        disabled={!targetConfig.troopType}
                                    >
                                        {levelChoices.map(level => (
                                            <MenuItem key={`${target.key}-level-${level}`} value={level}>
                                                {level}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                ) : (
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={t('Level')}
                                        type="number"
                                        value={targetConfig.level}
                                        onChange={event => handleLevelChange(target.key, event.target.value)}
                                        inputProps={{ min: 0, max: 99 }}
                                        sx={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
                                        disabled={!targetConfig.troopType}
                                    />
                                )}
                            </Box>

                            {selectedOption && (
                                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                                    <UnitIconFrame
                                        iconUrl={selectedOption.iconUrl}
                                        itemType="troop"
                                        level={targetConfig.level || selectedOption.maxLevel}
                                        size={34}
                                    />
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {`${getTroopLabel(selectedOption)} | ${buildOptionMetaLabel(selectedOption, t)}`}
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    </Paper>
                )
            })}
        </Box>
    )
}
